import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Flame, ArrowRight } from "lucide-react";
import { shopApi } from "@/lib/api";

export default function TrendingNow({ limit = 3, days = 30 }) {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["trending", limit, days],
    queryFn: () => shopApi.trending({ limit, days }),
    staleTime: 5 * 60 * 1000,
  });

  if (!isLoading && items.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 border-t border-white/5" data-testid="trending-now-section">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="chip mb-3 border-primary/40 text-primary">
            <Flame className="h-3 w-3" fill="currentColor" /> Trending now
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            What everyone's buying
          </h2>
        </div>
        <Link to="/shop?sort=rating" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="trending-see-more">
          See more <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="trending-grid">
        {items.slice(0, limit).map((p, i) => (
          <motion.div
            key={p.product_id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
          >
            <Link
              to={`/product/${p.slug}`}
              className="group block rounded-2xl bg-card border border-white/5 hover:border-primary/40 transition-all overflow-hidden card-hover"
              data-testid={`trending-item-${p.product_id}`}
            >
              <div className="flex sm:flex-col">
                <div className="relative w-32 sm:w-full aspect-square shrink-0 overflow-hidden bg-secondary/40">
                  <img
                    src={p.images?.[0]}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-primary">
                    <span className="w-4 h-4 rounded-full bg-primary text-black font-bold text-[10px] flex items-center justify-center">{i + 1}</span>
                    <Flame className="h-3 w-3" fill="currentColor" />
                    {p.sold_count > 0 ? `${p.sold_count} sold` : "Editor's pick"}
                  </span>
                </div>
                <div className="p-4 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{p.brand}</p>
                  <h3 className="text-sm font-medium leading-tight line-clamp-2 mb-2">{p.title}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-primary">${p.price.toFixed(2)}</span>
                    {p.compare_at_price && p.compare_at_price > p.price && (
                      <span className="text-xs text-muted-foreground line-through">${p.compare_at_price.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
